import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Settings as SettingsIcon, 
  Building2, 
  CreditCard, 
  FileText,
  Save,
  Plus,
  Trash2,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    business_name: '',
    address_line1: '',
    address_line2: '',
    phone: '',
    fax: '',
    email: '',
    abn: '',
    mvr_license: '',
    aircon_license: '',
    additional_licenses: [],
    bank_name: '',
    bsb: '',
    account_number: '',
    account_name: '',
    payment_instructions: '',
    default_service_note: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user
        const userResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (!userResponse.ok) {
          navigate('/login');
          return;
        }
        const userData = await userResponse.json();
        setUser(userData);

        // Owner (super_admin or shop admin) can access site settings (iter189)
        if (userData.role !== 'super_admin' && userData.role !== 'admin') {
          toast.error('Access denied');
          navigate('/');
          return;
        }

        // Fetch site settings
        const settingsResponse = await fetch(`${BACKEND_URL}/api/site-settings`, {
          credentials: 'include'
        });
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          setSettings(prev => ({
            ...prev,
            ...settingsData,
            additional_licenses: settingsData.additional_licenses || []
          }));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/site-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const addLicense = () => {
    setSettings(prev => ({
      ...prev,
      additional_licenses: [...prev.additional_licenses, { name: '', number: '' }]
    }));
  };

  const updateLicense = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      additional_licenses: prev.additional_licenses.map((lic, i) =>
        i === index ? { ...lic, [field]: value } : lic
      )
    }));
  };

  const removeLicense = (index) => {
    setSettings(prev => ({
      ...prev,
      additional_licenses: prev.additional_licenses.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="settings-container">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-bold text-3xl text-slate-900 mb-2">
              Site Configuration
            </h1>
            <p className="text-slate-500">
              Manage business details, licenses, and invoice settings
            </p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-settings-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        <Tabs defaultValue="business" className="space-y-6">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="business" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Business Details
            </TabsTrigger>
            <TabsTrigger value="licenses" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Licenses
            </TabsTrigger>
            <TabsTrigger value="banking" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Banking
            </TabsTrigger>
            <TabsTrigger value="invoicing" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Invoicing
            </TabsTrigger>
          </TabsList>

          {/* Business Details Tab */}
          <TabsContent value="business">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Business Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    value={settings.business_name || ''}
                    onChange={(e) => updateField('business_name', e.target.value)}
                    placeholder="e.g., ACME AUTO SERVICES"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address_line1">Address</Label>
                  <Input
                    id="address_line1"
                    value={settings.address_line1 || ''}
                    onChange={(e) => updateField('address_line1', e.target.value)}
                    placeholder="e.g., 123 Example Street, Melbourne, VIC, 3000"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={settings.phone || ''}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="e.g., 03 9000 0000"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="fax">Fax</Label>
                  <Input
                    id="fax"
                    value={settings.fax || ''}
                    onChange={(e) => updateField('fax', e.target.value)}
                    placeholder="e.g., 03 9000 0001"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={settings.email || ''}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="e.g., info@workshop.com.au"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="abn">ABN (Australian Business Number)</Label>
                  <Input
                    id="abn"
                    value={settings.abn || ''}
                    onChange={(e) => updateField('abn', e.target.value)}
                    placeholder="e.g., 12 345 678 901"
                    className="mt-1"
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Licenses Tab */}
          <TabsContent value="licenses">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                License Numbers
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                Manage your business licenses. These will automatically appear on all invoices.
              </p>
              
              <div className="space-y-6">
                {/* Standard Licenses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-slate-50 rounded-lg border">
                  <div>
                    <Label htmlFor="mvr_license" className="flex items-center gap-2">
                      RWC / LVT License Number
                      <span className="text-xs text-slate-400">(Motor Vehicle Repairer)</span>
                    </Label>
                    <Input
                      id="mvr_license"
                      value={settings.mvr_license || ''}
                      onChange={(e) => updateField('mvr_license', e.target.value)}
                      placeholder="e.g., AB12345"
                      className="mt-1"
                      data-testid="mvr-license-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="aircon_license" className="flex items-center gap-2">
                      Air Con License Number
                      <span className="text-xs text-slate-400">(Refrigerant Handling)</span>
                    </Label>
                    <Input
                      id="aircon_license"
                      value={settings.aircon_license || ''}
                      onChange={(e) => updateField('aircon_license', e.target.value)}
                      placeholder="e.g., AU000000"
                      className="mt-1"
                      data-testid="aircon-license-input"
                    />
                  </div>
                </div>

                {/* Additional Licenses */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium text-slate-900">Additional Licenses</h3>
                      <p className="text-sm text-slate-500">Add any other licenses you need to display on invoices</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={addLicense}
                      data-testid="add-license-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add License
                    </Button>
                  </div>

                  {settings.additional_licenses.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-lg border border-dashed">
                      <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No additional licenses added</p>
                      <p className="text-sm">Click "Add License" to add more license types</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {settings.additional_licenses.map((license, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                          <div className="flex-1">
                            <Input
                              value={license.name || ''}
                              onChange={(e) => updateLicense(index, 'name', e.target.value)}
                              placeholder="License Name (e.g., Electrical License)"
                              className="mb-2"
                            />
                            <Input
                              value={license.number || ''}
                              onChange={(e) => updateLicense(index, 'number', e.target.value)}
                              placeholder="License Number"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLicense(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Banking Tab */}
          <TabsContent value="banking">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-600" />
                Bank Details
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                Bank details displayed on invoices for customer payments
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={settings.bank_name || ''}
                    onChange={(e) => updateField('bank_name', e.target.value)}
                    placeholder="e.g., EXAMPLE BANK"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="account_name">Account Name</Label>
                  <Input
                    id="account_name"
                    value={settings.account_name || ''}
                    onChange={(e) => updateField('account_name', e.target.value)}
                    placeholder="e.g., ACME AUTO SERVICES PTY LTD"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="bsb">BSB</Label>
                  <Input
                    id="bsb"
                    value={settings.bsb || ''}
                    onChange={(e) => updateField('bsb', e.target.value)}
                    placeholder="e.g., 000-000"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input
                    id="account_number"
                    value={settings.account_number || ''}
                    onChange={(e) => updateField('account_number', e.target.value)}
                    placeholder="e.g., 12345678"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="payment_instructions">Payment Instructions</Label>
                  <Textarea
                    id="payment_instructions"
                    value={settings.payment_instructions || ''}
                    onChange={(e) => updateField('payment_instructions', e.target.value)}
                    placeholder="e.g., Please email your transaction receipt to info@workshop.com.au. DEBIT/CREDIT CARD Pay on collection of your vehicle."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Invoicing Tab */}
          <TabsContent value="invoicing">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Invoice Defaults
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                Default settings for new invoices
              </p>
              
              <div>
                <Label htmlFor="default_service_note">Default Service Note</Label>
                <p className="text-xs text-slate-400 mb-2">
                  This note will be pre-filled on new invoices
                </p>
                <Textarea
                  id="default_service_note"
                  value={settings.default_service_note || ''}
                  onChange={(e) => updateField('default_service_note', e.target.value)}
                  placeholder="Enter default service note..."
                  className="mt-1"
                  rows={5}
                />
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
